<script setup lang="ts">
import { computed, useTemplateRef, watch } from "vue";

import { TeamBoard } from "@/board/TeamBoard";
import {
  EMPTY_INDEX,
  OBJECTIVES_INDEX,
  RISKS_INDEX,
  TeamBoardLocation,
} from "@/board/location/TeamBoardLocation";
import StickyNote from "@/components/StickyNote/StickyNote.vue";
import TeamObjectives from "@/components/objectives/TeamObjectives.vue";
import LoadingIndicator from "@/components/ui/LoadingIndicator.vue";
import SvgIcon from "@/components/ui/SvgIcon/SvgIcon.vue";
import { relativeClientCoord } from "@/math/coordinate-utils";
import type { IdMap } from "@/model/baseTypes";
import type { BoardCard } from "@/model/card";
import { normalLinkColors } from "@/model/colors";
import type { RelativeCoordinate } from "@/model/coordinates";
import { useBoardsStore } from "@/store/boards";
import { useSessionStore } from "@/store/session";
import { useTeamStore } from "@/store/team";
import { blurHandlerElem } from "@/utils/dom/dom";
import { isEmpty } from "@/utils/object";

import LinkLayers from "./internal/LinkLayer/LinkLayers.vue";
import TeamIteration from "./internal/TeamIteration.vue";
import { useFluidBoard } from "./internal/useFluidBoard";

const board = computed(() => useBoardsStore().boardByType("team"));
const rootRef = useTemplateRef("root");

const { active, isLoading, levelOfDetails, readonly, focusAndBlur, boardSize } = useFluidBoard(
  board,
  rootRef,
);

defineExpose(focusAndBlur);

// Location Indexes for non-iteration locations
const locationIndexes = { risks: RISKS_INDEX, objectives: OBJECTIVES_INDEX, empty: EMPTY_INDEX };

// We are using this instead of board.cards since we want to show them
// in batches to avoid blocking the main thread.
let deferredStickies: IdMap<BoardCard> = {};

const iterations = computed(() => useSessionStore().iterations);
const columns = computed(() => {
  // iterations can be temporarily empty
  const iters = iterations.value.length;
  return iters < 2 ? 0 : Math.ceil(1 + iters / 2);
});
const fieldWidth = computed(() => boardSize.value.x / columns.value);

const stickies = computed(() => (isEmpty(deferredStickies) ? board.value.cards : deferredStickies));

watch(
  () => useTeamStore().current,
  () => {
    if (board.value.type !== "team" || board.value.state.state !== "loaded") return;
    //  Reset deferred stickies when team changes
    deferredStickies = {};
    void loadStickiesInBatches();
  },
  { immediate: true },
);

async function loadStickiesInBatches() {
  const cards = Object.entries(board.value.cards);
  let currentIndex = 0;
  const batchSize = 50;

  const processNextBatch = () => {
    return new Promise<void>((resolve) => {
      requestAnimationFrame(() => {
        const batch = cards.slice(currentIndex, currentIndex + batchSize);
        batch.forEach(([id, card]) => {
          deferredStickies[id] = card;
        });
        resolve();
      });
    });
  };

  while (currentIndex < cards.length) {
    await processNextBatch();
    currentIndex += batchSize;
  }

  // Once all stickies are loaded, clear deferredStickies so the user interacts with the board stickies again
  deferredStickies = {};
}

function location(c: RelativeCoordinate | number) {
  return TeamBoardLocation.of(board.value.iterationStates, c);
}

function zoomToRegionHandler(e: MouseEvent) {
  const boardLogic = new TeamBoard(board.value);
  const loc = location(relativeClientCoord(e));
  boardLogic.zoomAction(loc)?.("mouse");
}
</script>

<!-- eslint-disable vuejs-accessibility/click-events-have-key-events vuejs-accessibility/no-static-element-interactions-->
<template>
  <div
    :id="board.id"
    ref="root"
    class="board board-team"
    :aria-label="$t('board.team')"
    tabindex="0"
    @click="blurHandlerElem"
  >
    <LoadingIndicator v-if="active && isLoading" global />
    <LinkLayers :board :color="normalLinkColors.team" />
    <div class="backdrop" @dblclick="zoomToRegionHandler">
      <TeamIteration
        v-for="i in columns"
        :key="i"
        v-owned-cards="{ board, location: location(iterations[i - 1].id) }"
        :index="i - 1"
        :readonly
        class="field"
        style="height: 50%"
        :style="{ left: (i - 1) * fieldWidth + 'px', width: fieldWidth + 'px' }"
      />
      <!-- pointerdown.stop to avoid sticky create by long click -->
      <section
        v-board-region
        v-owned-cards="{ board, location: location(locationIndexes.objectives) }"
        class="field objectives"
        style="height: 50%; top: 50%"
        :style="{ width: fieldWidth + 'px' }"
        :aria-label="$t('teamObjectives.teamObjectives')"
        @click="blurHandlerElem"
        @pointerdown.stop
      >
        <TeamObjectives />
      </section>
      <section
        v-board-region
        v-owned-cards="{ board, location: location(locationIndexes.risks) }"
        class="field risks"
        style="height: 50%; top: 50%"
        :style="{ left: fieldWidth + 'px', width: fieldWidth + 'px' }"
        aria-labelledby="team-board-risks-header"
        @click="blurHandlerElem"
      >
        <h2 id="team-board-risks-header" class="header h3">
          <SvgIcon name="board/risk" width="1.25em" height="1.25em" aria-hidden="true" />
          <span>{{ $t("teamBoardLocation.risks") }}</span>
        </h2>
      </section>
      <TeamIteration
        v-for="i in iterations.length - columns"
        :key="i + columns"
        v-owned-cards="{ board, location: location(iterations[i + columns - 1].id) }"
        :index="i - 1 + columns"
        :readonly
        class="field"
        style="height: 50%; top: 50%"
        :style="{ left: (i + 1) * fieldWidth + 'px', width: fieldWidth + 'px' }"
      />
    </div>
    <StickyNote
      v-for="card in stickies"
      :key="card.data.id"
      :card="card.data"
      :card-meta="card.meta"
      :level-of-details
    />
  </div>
</template>

<style lang="scss">
@use "@/styles/font";
@use "@/styles/board";
@use "@/styles/variables/colors";
@use "@/styles/mixins/a11y";

.items-center {
  align-items: center;
}

.board-team {
  @include a11y.board;

  position: relative;

  h2,
  h3,
  h4 {
    display: inline-block;
    vertical-align: middle;
  }

  img.icon {
    height: board.len(28px);
    vertical-align: middle;
    border-radius: board.len(4px);
  }

  .field {
    padding: board.len(12px);
  }

  .field.risks {
    @include a11y.board-section;

    color: colors.$alternative-red;
    font-size: 80%;

    h2 {
      font-weight: font.$weight-bold;
      display: flex;
      gap: 0.5em;
      align-items: center;
      margin-top: 0.5em;
    }

    span {
      vertical-align: middle;
    }
  }

  .objectives {
    @include a11y.board-section;

    padding: 3px 4px 5px 5px;

    .header {
      h3 {
        font-size: board.len(16px);
        font-weight: font.$weight-bold;
      }
    }
  }

  .cards-skip-button {
    top: board.len(80px) !important;
  }

  #team-board-risks-header {
    & > svg,
    & > span {
      @include board.board-control;
    }
  }
}
</style>
